import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatFileSize } from "@/lib/format";
import {
  FEEDBACK_ALLOWED_MIME_TYPES,
  FEEDBACK_MAX_ATTACHMENTS,
  FEEDBACK_MAX_ATTACHMENT_BYTES,
} from "@/lib/feedback-attachments";
import { cn } from "@/lib/utils";

interface FeedbackAttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

const ACCEPT_ATTR = FEEDBACK_ALLOWED_MIME_TYPES.join(",");

/**
 * Drag-drop sone + filvelger for vedlegg på FeedbackDialog (VIB-691).
 *
 * Validerer maks 10 filer, maks 20 MB per fil, og bare bilder/PDF. Viser
 * thumbnails for bilder og filnavn + størrelse for PDF. Validering kjøres
 * også i `useSendFeedback`-hooken som backup, men her gir vi øyeblikkelig
 * feedback til brukeren før de prøver å sende.
 */
export default function FeedbackAttachmentPicker({
  files,
  onChange,
  disabled,
}: FeedbackAttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lager blob-URLer for bilde-thumbs. Revoker når komponenten unmountes
  // eller filer endres, slik at vi ikke lekker minne i lange sesjoner.
  const previews = useMemo(
    () =>
      files.map((file) => ({
        file,
        url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    [files],
  );

  useEffect(() => {
    return () => {
      for (const p of previews) {
        if (p.url) URL.revokeObjectURL(p.url);
      }
    };
  }, [previews]);

  const validateAndAdd = useCallback(
    (incoming: FileList | File[]) => {
      const incomingArr = Array.from(incoming);
      const newFiles: File[] = [];
      const errors: string[] = [];

      for (const file of incomingArr) {
        if (
          !FEEDBACK_ALLOWED_MIME_TYPES.includes(
            file.type as (typeof FEEDBACK_ALLOWED_MIME_TYPES)[number],
          )
        ) {
          errors.push(`${file.name}: ikke støttet filtype`);
          continue;
        }
        if (file.size > FEEDBACK_MAX_ATTACHMENT_BYTES) {
          errors.push(`${file.name}: større enn 20 MB`);
          continue;
        }
        newFiles.push(file);
      }

      const combined = [...files, ...newFiles];
      if (combined.length > FEEDBACK_MAX_ATTACHMENTS) {
        errors.push(`Maks ${FEEDBACK_MAX_ATTACHMENTS} filer per feedback`);
      }
      const truncated = combined.slice(0, FEEDBACK_MAX_ATTACHMENTS);

      setError(errors.length ? errors.join(" • ") : null);
      onChange(truncated);
    },
    [files, onChange],
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndAdd(e.target.files);
    }
    // Reset input slik at samme fil kan velges igjen etter fjerning
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndAdd(e.dataTransfer.files);
    }
  };

  const handleRemove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    onChange(next);
    setError(null);
  };

  const atLimit = files.length >= FEEDBACK_MAX_ATTACHMENTS;

  // VIB-960: lim inn bilde fra utklippstavla (Ctrl/Cmd+V) → legg til som vedlegg.
  // Global lytter mens picker er montert; ignorerer tekst-paste (kun bilde-filer
  // håndteres, så paste i tekstfeltet fungerer som normalt).
  useEffect(() => {
    if (disabled || atLimit) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.files;
      if (!items || items.length === 0) return;
      const images = Array.from(items).filter((f) => f.type.startsWith("image/"));
      if (images.length === 0) return;
      e.preventDefault();
      validateAndAdd(images);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [disabled, atLimit, validateAndAdd]);

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Last opp vedlegg"
        onClick={() => !disabled && !atLimit && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled || atLimit) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !atLimit) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-1 rounded-md border-[1.5px] border-dashed px-3 py-4 text-center text-sm transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-border bg-muted/30",
          (disabled || atLimit) && "cursor-not-allowed opacity-60",
          !disabled && !atLimit && "cursor-pointer hover:border-foreground/40",
        )}
      >
        <SrIcon name="paperclip" className="h-5 w-5 text-muted-foreground" />
        <div className="text-muted-foreground">
          {atLimit ? (
            <>Du har nådd grensen på {FEEDBACK_MAX_ATTACHMENTS} filer</>
          ) : (
            <>
              <span className="font-medium text-foreground">Klikk, dra inn eller lim inn</span>{" "}
              skjermbilder eller PDF
            </>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          Maks {FEEDBACK_MAX_ATTACHMENTS} filer, 20 MB per fil
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled || atLimit}
        />
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {previews.map((p, idx) => (
            <li
              key={`${p.file.name}-${idx}`}
              className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5"
            >
              {p.url ? (
                <img
                  src={p.url}
                  alt={p.file.name}
                  className="h-9 w-9 rounded object-cover"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded bg-muted">
                  <SrIcon name="file" className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{p.file.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(p.file.size)}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(idx)}
                disabled={disabled}
                aria-label={`Fjern ${p.file.name}`}
              >
                <SrIcon name="x" className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
